import moment from "moment-timezone";

// Queries
import FETCH_PROGRAMS from "../../graphql/query/FetchPrograms.gql";
import FETCH_PROGRAM_DETAILS from "../../graphql/query/FetchProgramDetails.gql";
import RUN_PROGRAM_ALGORITHM from "../../graphql/query/FetchAlgorithmResults.gql";
import FETCH_FEATURE_BY_NAME from "../../graphql/query/FetchFeatureByName.gql";
import FETCH_FEATURES_CONFIG from "../../graphql/query/FetchFeaturesConfig.gql";
import DOWNLOAD_PROGRAM_REPORT from "../../graphql/query/DownloadProgramReport.gql";

// Mutations
import CREATE_PROGRAM from "../../graphql/mutation/CreateProgram.gql";
import REMOVE_PROGRAM from "../../graphql/mutation/RemoveProgram.gql";
import UNLINK_COACH from "../../graphql/mutation/UnlinkCoach.gql";
import UPDATE_PROGRAM from "../../graphql/mutation/UpdateProgram.gql";

export const namespaced = true;

const initState = () => {
  return {
    programs: [],
    isProgramsLoading: false,
    features: [],
    featuresConfig: [],
  };
};

export const state = initState();

export const mutations = {
  SET_PROGRAMS(state, programs) {
    state.programs = programs;
  },
  SET_LOADING(state, status) {
    state.isProgramsLoading = status;
  },
  REMOVE_PROGRAM(state, removedProgramId) {
    state.programs = state.programs.filter(({ _id }) => _id !== removedProgramId);
  },
  SET_FEATURES_CONFIG(state, featuresConfig) {
    state.featuresConfig = featuresConfig;
  },
};

export const actions = {
  async createProgram(
    { commit, dispatch },
    { apolloClient, newProgram, organizationId }
  ) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { CreateProgram: program },
      } = await apolloClient.mutate({
        mutation: CREATE_PROGRAM,
        fetchPolicy: "no-cache",
        variables: {
          data: newProgram,
        },
      });

      if (!program) throw new Error("No se pudo crear el programa");

      // If an organization ID is provided, associate the program with it
      if (organizationId) {
        try {
          await dispatch(
            "organizations/addProgramToOrganization",
            {
              organizationId,
              programId: program._id,
              apolloClient,
            },
            { root: true }
          );
        } catch (orgError) {
          console.error("Error associating program with organization:", orgError);
          // We continue even if this fails, as the program was created successfully
        }
      }

      return program;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async fetchFeaturesConfig({ commit }, { apolloClient }) {
    try {
      const {
        data: { FetchFeaturesConfig: featuresConfig },
      } = await apolloClient.query({
        query: FETCH_FEATURES_CONFIG,
        fetchPolicy: "no-cache",
      });
      commit("SET_FEATURES_CONFIG", featuresConfig);
      return featuresConfig;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async fetchProgramDetails({ commit }, { _id, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { FetchProgramDetails: programDetails },
      } = await apolloClient.query({
        query: FETCH_PROGRAM_DETAILS,
        fetchPolicy: "no-cache",
        variables: {
          data: {
            filter: {
              _id,
            },
          },
        },
      });
      return programDetails;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async runProgramAlgorithm({ commit }, { programId, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { FetchAlgorithmResults: AlgorithmResults },
      } = await apolloClient.query({
        query: RUN_PROGRAM_ALGORITHM,
        fetchPolicy: "no-cache",
        variables: {
          ID: programId,
        },
      });
      return AlgorithmResults;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async fetchPrograms(
    { commit },
    { filter, limit, skip, apolloClient, restClient }
  ) {
    try {
      commit("SET_LOADING", true);
      if (restClient) {
        const path = `${
          process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
        }/programs`;
        const programs = await restClient.get(path);
        commit("SET_PROGRAMS", programs.data);
        return programs.data;
      }

      const {
        data: { FetchPrograms: programs },
      } = await apolloClient.query({
        query: FETCH_PROGRAMS,
        fetchPolicy: "no-cache",
        variables: {
          data: {
            filter,
            limit,
            skip,
          },
        },
      });

      commit("SET_PROGRAMS", programs);
      return programs;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async removeProgram({ commit }, { _id, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { RemoveProgram: removedProgram },
      } = await apolloClient.query({
        query: REMOVE_PROGRAM,
        fetchPolicy: "no-cache",
        variables: {
          data: {
            _id,
          },
        },
      });
      if (!removedProgram._id)
        throw new Error("Hubo un error eliminando el programa");
      commit("REMOVE_PROGRAM", removedProgram._id);
      return removedProgram;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async unlinkCoach({ commit }, { programId, coachId, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { UnlinkCoach: updatedProgram },
      } = await apolloClient.mutate({
        mutation: UNLINK_COACH,
        fetchPolicy: "no-cache",
        variables: { coachId, programId },
      });
      return updatedProgram;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async updateProgram({ commit }, { data, _id, apolloClient }) {
    try {
      commit("SET_LOADING", true);
      const {
        data: { UpdateProgram: updatedProgram },
      } = await apolloClient.mutate({
        mutation: UPDATE_PROGRAM,
        fetchPolicy: "no-cache",
        variables: { data, _id },
      });
      return updatedProgram;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async fetchFeatureByName({ commit }, { name, programId, apolloClient }) {
    try {
      const {
        data: { FetchFeatureByName: feature },
      } = await apolloClient.query({
        query: FETCH_FEATURE_BY_NAME,
        fetchPolicy: "no-cache",
        variables: {
          name,
          programId,
        },
      });
      return feature;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  async generateReport(
    { commit },
    { programId, programIds, apolloClient, restClient }
  ) {
    try {
      if (restClient) {
        const path = `${
          process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
        }/reports-dashboard/programs`;
        const response = await restClient.post(path, {
          programIds,
        });

        return response;
      }
      const {
        data: { DownloadProgramReport },
      } = await apolloClient.query({
        query: DOWNLOAD_PROGRAM_REPORT,
        fetchPolicy: "no-cache",
        variables: {
          programId,
        },
      });
      return DownloadProgramReport;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  async translateCoachBios(_, { programId, lang, force, coachIds, restClient }) {
    const path = `${
      process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
    }/programs/${programId}/translate-coaches`;
    return restClient.post(path, { lang, force, coachIds });
  },

  async fetchCoacheesForReports(_, { programId, organizationId, restClient }) {
    try {
      const path = `${
        process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
      }/reports-dashboard/individual-reports/coachees`;
      const params = programId ? { programId } : { organizationId };
      const response = await restClient.get(path, { params });
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async generateIndividualReports({ dispatch }, { items, restClient }) {
    const basePath = `${
      process.env?.VUE_APP_BASE_REST_API_PATH || "/api/v1"
    }/reports-dashboard/individual-reports`;

    // POST starts the job — backend responds immediately with a jobId
    const { jobId } = await restClient.post(basePath, { items });

    // Show persistent blue toast while the job runs in the background
    dispatch(
      "notifications/callSnackbar",
      {
        color: "info",
        content: "Generando reportes...",
        timeout: -1,
        showClose: false,
        contentClass: "white--text text-h3 font-weight-medium",
      },
      { root: true }
    );

    // Start polling without blocking — snackbar updates happen inside the helper
    void _pollReportJob({ jobId, basePath, restClient, dispatch }).catch((error) => {
      console.error(error);
      dispatch(
        "notifications/callSnackbar",
        {
          color: "error",
          content: "Ocurrió un error inesperado al generar los reportes",
          timeout: 7000,
          showClose: true,
          contentClass: "text-h3 font-weight-medium",
        },
        { root: true }
      );
    });
  },
};

// ─── Individual report polling helpers ───────────────────────────────────────

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 75; // 5 minutes max (75 × 4 s)

async function _pollReportJob({ jobId, basePath, restClient, dispatch }) {
  function showSnackbar(options) {
    dispatch(
      "notifications/callSnackbar",
      { contentClass: "text-h3 font-weight-medium", ...options },
      { root: true }
    );
  }

  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    try {
      const { status, result, error } = await restClient.get(`${basePath}/${jobId}`);

      if (status === "completed") {
        _downloadResult(result);
        showSnackbar({
          color: "success",
          content: "Reportes generados correctamente",
          timeout: 5000,
          showClose: false,
        });
        return;
      }

      if (status === "failed") {
        showSnackbar({
          color: "error",
          content: error || "Error al generar los reportes",
          timeout: 7000,
          showClose: true,
        });
        return;
      }

      // status === 'processing' — keep polling
    } catch (_err) {
      showSnackbar({
        color: "error",
        content: "Error al obtener el estado de los reportes",
        timeout: 7000,
        showClose: true,
      });
      return;
    }
  }

  // Exceeded max attempts
  dispatch(
    "notifications/callSnackbar",
    {
      color: "error",
      content: "La generación de reportes tardó demasiado",
      timeout: 7000,
      showClose: true,
      contentClass: "text-h3 font-weight-medium",
    },
    { root: true }
  );
}

function _downloadResult(data) {
  if (!data.pdfs || !data.pdfs.length) return;
  // Stagger downloads 400 ms apart — browsers silently block simultaneous programmatic triggers
  data.pdfs.forEach((item, idx) => {
    setTimeout(() => {
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${item.pdf}`;
      link.download = item.filename;
      link.click();
    }, idx * 400);
  });
}

// ─────────────────────────────────────────────────────────────────────────────

export const getters = {
  getProgramById: (state, getters) => (programId) =>
    getters.programs?.find((program) => program?._id === programId) ?? null,
  isProgramsLoading: ({ isProgramsLoading }) => isProgramsLoading,
  programs: ({ programs }) => programs || [],
  features: (state) => state?.features || [],
  featuresConfig: (state) => state?.featuresConfig || [],
};
